import type {LibraryState, StudentAttempt} from "./activity-library";
import {attemptSessionId} from "./activity-continuity";

export const copyReleased = (a: StudentAttempt) => a.status === "completed" && a.restitution?.status === "released";
/** Missing metadata deliberately means hidden, including historical copies. */
export function canConsultCopy(s: Pick<LibraryState, "digitalSessions">, a: StudentAttempt) {
  if (copyReleased(a)) return true;
  if (!["not_started", "in_progress"].includes(a.status)) return false;
  const sessionId = attemptSessionId(a);
  return !sessionId || !!s.digitalSessions?.some(d => d.id === sessionId && d.access !== "ended");
}
export const evidenceLocked = (a: StudentAttempt, blockId: string) =>
  Object.values(a.checkpointValidations ?? {}).some(v => v.locked && v.evidenceBlockIds.includes(blockId));

/** A finalized copy stays immutable except for its first release or a teacher-only display rotation of one attached photo. */
export function isRestitutionOnlyChange(before: StudentAttempt, after: StudentAttempt) {
  if (before.status !== "completed" || after.status !== "completed" || after.revision !== before.revision + 1) return false;

  if (before.restitution?.status !== "released" &&
      after.restitution?.status === "released" && after.restitution.releasedAt && after.restitution.releasedBy) {
    const strip = (a: StudentAttempt) => {const c = {...a}; delete c.restitution; return {...c, revision: 0};};
    if (JSON.stringify(strip(before)) === JSON.stringify(strip(after))) return true;
  }

  const rotations = (attempt: StudentAttempt) => Object.fromEntries(
    Object.values(attempt.photos ?? {}).flat().map(photo => [photo.id, photo.rotation ?? 0]),
  );
  const beforeRotations = rotations(before), afterRotations = rotations(after);
  if (Object.keys(beforeRotations).length !== Object.keys(afterRotations).length ||
      Object.keys(beforeRotations).some(id => !Object.hasOwn(afterRotations,id))) return false;
  const changed = Object.keys(afterRotations).filter(id => beforeRotations[id] !== afterRotations[id]);
  if (changed.length !== 1) return false;
  const photoId = changed[0];
  if (![0,90,180,270].includes(beforeRotations[photoId]) ||
      afterRotations[photoId] !== (beforeRotations[photoId] + 90) % 360) return false;

  const beforeAnnotation = before.photoAnnotations?.[photoId];
  const afterAnnotation = after.photoAnnotations?.[photoId];
  if (beforeAnnotation) {
    if (!afterAnnotation) return false;
    const expectedStrokes = beforeAnnotation.strokes.map(stroke => ({
      ...stroke,
      points: stroke.points.map(point => ({x: 1 - point.y, y: point.x})),
    }));
    if (JSON.stringify(afterAnnotation.strokes) !== JSON.stringify(expectedStrokes) || !afterAnnotation.updatedAt) return false;
  } else if (afterAnnotation) return false;

  const normalize = (attempt: StudentAttempt) => {
    const copy = structuredClone(attempt);
    copy.revision = 0;
    for (const photos of Object.values(copy.photos ?? {}))
      for (const photo of photos) if (photo.id === photoId) photo.rotation = 0;
    if (copy.photoAnnotations?.[photoId]) delete copy.photoAnnotations[photoId];
    return copy;
  };
  return JSON.stringify(normalize(before)) === JSON.stringify(normalize(after));
}
